import React, { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import copy from 'copy-to-clipboard';

import {  ShareLinkFormType } from '../../form/link-config-form.factory';
import { Button } from 'src/common/button';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { apiGetJourneyAlias } from 'src/utils/journeyApi';
import { generateJourneyAliasShareUrl } from 'src/utils/journey';
import { useCurrentOrganization } from 'src/store/organization';

export const ShareConfigUTMParamsPanel = () => {
  const { values, setFieldValue } = useFormikContext<ShareLinkFormType>();
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const { currentOrganization } = useCurrentOrganization();
  const [organizationSlug, setOrganizationSlug] = useState('');

  // Load organization_slug from backend
  useEffect(() => {
    const fetchAliasSettings = async () => {
      try {
        if (values.uuid) {
          const response = await apiGetJourneyAlias(values.uuid);
          if (response && response.org_slug) {
            setOrganizationSlug(response.org_slug);
          }
        }
      } catch (error) {
        console.error('Error loading alias settings:', error);
      }
    };

    fetchAliasSettings();
  }, [values.uuid]);


  const handleUtmSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUtmSource(e.target.value);
  };

  const handleUtmMediumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUtmMedium(e.target.value);
  };

  const handleUtmCampaignChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUtmCampaign(e.target.value);
  };

  const handleUtmTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUtmTerm(e.target.value);
  };

  const handleUtmContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUtmContent(e.target.value);
  };

  const generateUtmUrl = () => {
    // Generate the base URL
    const baseUrl = generateJourneyAliasShareUrl(currentOrganization, values.slug);
    
    // Build UTM parameters
    const utmParams = [];
    
    if (utmSource) utmParams.push(`utm_source=${encodeURIComponent(utmSource)}`);
    if (utmMedium) utmParams.push(`utm_medium=${encodeURIComponent(utmMedium)}`);
    if (utmCampaign) utmParams.push(`utm_campaign=${encodeURIComponent(utmCampaign)}`);
    if (utmTerm) utmParams.push(`utm_term=${encodeURIComponent(utmTerm)}`);
    if (utmContent) utmParams.push(`utm_content=${encodeURIComponent(utmContent)}`);
    
    if (utmParams.length === 0) {
      setErrorNotification('Please fill in at least one UTM parameter');
      return;
    }
    
    // Combine URL and parameters
    const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${utmParams.join('&')}`;
    setGeneratedUrl(url);
    setSuccessNotification('UTM URL generated');
  };

  const copyToClipboard = () => {
    if (!generatedUrl) {
      setErrorNotification('Generate a UTM URL first');
      return;
    }
    copy(generatedUrl);
    setSuccessNotification('UTM URL copied to clipboard');
  };

  const clearForm = () => {
    setUtmSource('');
    setUtmMedium('');
    setUtmCampaign('');
    setUtmTerm('');
    setUtmContent('');
    setGeneratedUrl('');
  };

  return (
    <div className='flex flex-col divide-y'>
      <div className='py-6'>
        <div className='text-bedrock-p-strong mb-2'>UTM Parameters</div>
        <div className='text-bedrock-p text-bedrock-gray-dark mb-4'>
          Create URLs with UTM parameters for tracking campaigns in journey insights.
        </div>
        
      

        
          <div className='mt-6 border-t pt-6'>
            <div className='text-bedrock-p-strong mb-2'>Generate UTM URL</div>
            <div className='text-bedrock-p text-bedrock-gray-dark mb-4'>
              Create URLs with UTM parameters to track the journeys
            </div>
            
            <div className='flex flex-col space-y-4 mb-6'>
              <div>
                <label htmlFor='utm-source' className='block text-bedrock-p-small mb-1'>
                  UTM Source <span className='text-bedrock-gray-dark'>(e.g. google, newsletter)</span>
                </label>
                <input
                  id='utm-source'
                  type='text'
                  className='input-bedrock-text w-full'
                  placeholder='Source'
                  value={utmSource}
                  onChange={handleUtmSourceChange}
                />
              </div>
              
              <div>
                <label htmlFor='utm-medium' className='block text-bedrock-p-small mb-1'>
                  UTM Medium <span className='text-bedrock-gray-dark'>(e.g. cpc, email)</span>
                </label>
                <input
                  id='utm-medium'
                  type='text'
                  className='input-bedrock-text w-full'
                  placeholder='Medium'
                  value={utmMedium}
                  onChange={handleUtmMediumChange}
                />
              </div>

              <div>
                <label htmlFor='utm-campaign' className='block text-bedrock-p-small mb-1'>
                  UTM Campaign <span className='text-bedrock-gray-dark'>(e.g. black_friday_sale)</span>
                </label>
                <input
                  id='utm-campaign'
                  type='text'
                  className='input-bedrock-text w-full'
                  placeholder='Campaign'
                  value={utmCampaign}
                  onChange={handleUtmCampaignChange}
                />
              </div>

              <div>
                <label htmlFor='utm-term' className='block text-bedrock-p-small mb-1'>
                  UTM Term <span className='text-bedrock-gray-dark'>(optional, e.g. standard+plan)</span>
                </label>
                <input
                  id='utm-term'
                  type='text'
                  className='input-bedrock-text w-full'
                  placeholder='Term'
                  value={utmTerm}
                  onChange={handleUtmTermChange}
                />
              </div>

              <div>
                <label htmlFor='utm-content' className='block text-bedrock-p-small mb-1'>
                  UTM Content <span className='text-bedrock-gray-dark'>(optional, e.g. textlink, banner_top)</span>
                </label>
                <input
                  id='utm-content'
                  type='text'
                  className='input-bedrock-text w-full'
                  placeholder='Content'
                  value={utmContent}
                  onChange={handleUtmContentChange}
                />
              </div>
            </div>

            <div className='flex flex-col space-y-4'>
              <Button variant='primary' size='regular' label='Generate UTM URL' className='w-full' onClick={generateUtmUrl} />
              
              {generatedUrl && (
                <>
                  <div className='mt-4'>
                    <label className='block text-bedrock-p-small mb-1'>Generated URL</label>
                    <div className='p-2 bg-gray-100 rounded border text-sm break-all overflow-hidden'>
                      {generatedUrl}
                    </div>
                  </div>
                  
                  <div className='flex space-x-2'>
                    <Button variant='secondary' size='regular' label='Copy URL' className='w-full' onClick={copyToClipboard} />
                    <Button variant='secondary' size='regular' label='Clear Form' className='w-full' onClick={clearForm} />
                  </div>
                </>
              )}
            </div>
          </div>
        
      </div>
    </div>
  );
}; 